import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Webhook, Copy, Save, PlugZap, RefreshCw, ShieldCheck, Smartphone, FileCheck, Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errMsg, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/settings/Card";
import { ConnectWhatsApp } from "@/components/settings/ConnectWhatsApp";
import { PhoneStatusBadge } from "@/components/PhoneStatusBadge";
import { PhoneReminder } from "@/components/settings/PhoneReminder";

const FIELDS = [
  { k: "wa_access_token", label: "Meta Token di accesso permanente", ph: "EAAG…", secret: true },
  { k: "wa_business_account_id", label: "ID account WhatsApp Business (WABA ID)", ph: "1234567890" },
  { k: "wa_phone_number_id", label: "ID numero di telefono", ph: "1098765432" },
  { k: "wa_app_id", label: "Meta App ID (per Embedded Signup e logo profilo)", ph: "App ID" },
  { k: "wa_es_config_id", label: "Configuration ID Embedded Signup (Facebook Login for Business)", ph: "config_id" },
  { k: "wa_app_secret", label: "App Secret (scambio codice e verifica firma webhook)", ph: "App secret", secret: true },
];

const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copiato negli appunti"); };

const CoexSync = ({ data }) => {
  const qc = useQueryClient();
  const run = async (type) => {
    if (!window.confirm("Meta permette questa sincronizzazione UNA sola volta, entro 24 ore dal collegamento. Procedere?")) return;
    try { await api.post(`/settings/coexistence/sync/${type}`); toast.success("Sincronizzazione avviata"); qc.invalidateQueries({ queryKey: ["settings"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const fmt = (v) => (v ? new Date(v).toLocaleString("it-IT") : null);
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="coex-sync-panel">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sincronizzazione iniziale (una volta sola)</div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="coex-sync-contacts-button" variant="outline" size="sm" disabled={!!data.coex_smb_app_state_sync_started_at} onClick={() => run("smb_app_state_sync")}>1. Sincronizza rubrica</Button>
        <Button data-testid="coex-sync-history-button" variant="outline" size="sm" disabled={!!data.coex_history_started_at} onClick={() => run("history")}>2. Importa storico (180 gg)</Button>
      </div>
      <ul className="text-xs space-y-0.5" data-testid="coex-sync-status">
        <li>Rubrica: {fmt(data.coex_smb_app_state_sync_started_at) ? `avviata il ${fmt(data.coex_smb_app_state_sync_started_at)}` : "non avviata"}</li>
        <li>Storico: {fmt(data.coex_history_started_at) ? `avviato il ${fmt(data.coex_history_started_at)} · ${data.coex_history_progress ?? 0}% · ${data.coex_history_imported ?? 0} messaggi importati` : "non avviato"}</li>
        {data.coex_history_error && <li className="text-red-600">{data.coex_history_error}</li>}
      </ul>
    </div>
  );
};

export const WhatsAppSettings = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [f, setF] = useState(null);
  const [testing, setTesting] = useState(false);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (!f) return null;
  const webhookUrl = `${BACKEND_URL}/api/webhook/whatsapp`;

  const save = async () => {
    try {
      const body = Object.fromEntries([...FIELDS.map((x) => x.k), "wa_verify_token"].map((k) => [k, f[k]]));
      const { data: s } = await api.put("/settings", body);
      qc.setQueryData(["settings"], s);
      toast.success("Credenziali salvate");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data: r } = await api.post("/settings/whatsapp/test");
      r.ok ? toast.success(r.detail) : toast.error(r.detail);
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };
  const genToken = () => setF({ ...f, wa_verify_token: crypto.randomUUID().replace(/-/g, "") });

  return (
    <div className="space-y-6">
      <Card testid="waba-credentials-card" title="Credenziali Meta" desc="Da Meta for Developers → WhatsApp → Configurazione API" icon={KeyRound}
        right={<span data-testid="waba-status-badge" className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 ${data.wa_configured ? "bg-[#DCFCE7] text-[#15803D]" : "bg-orange-100 text-orange-700"}`}>
          <i className={`w-1.5 h-1.5 rounded-full ${data.wa_configured ? "bg-[#25D366]" : "bg-orange-500"}`} />{data.wa_configured ? "Configurato" : "Modalità simulazione"}</span>}>
        <div className="space-y-4">
          {FIELDS.map(({ k, label, ph, secret }) => (
            <div key={k} className="space-y-1.5">
              <Label>{label}</Label>
              <Input data-testid={`waba-${k.replace(/_/g, "-")}-input`} type={secret && !f[k]?.startsWith("•") ? "password" : "text"} value={f[k] || ""} placeholder={ph}
                onFocus={() => secret && f[k]?.startsWith("•") && setF({ ...f, [k]: "" })} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="font-mono text-sm" />
            </div>
          ))}
          {!data.wa_configured && <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">Senza credenziali i messaggi vengono salvati come <b>simulati</b> e non raggiungono il cliente.</p>}
          <div className="flex justify-end gap-2">
            <Button data-testid="waba-test-connection-button" variant="outline" onClick={test} disabled={testing}><PlugZap size={15} className="mr-1.5" />{testing ? "Verifica…" : "Verifica connessione"}</Button>
            <Button data-testid="waba-save-settings-button" onClick={save} className="bg-[#0F172A] hover:bg-[#1E293B]"><Save size={15} className="mr-1.5" />Salva credenziali</Button>
          </div>
        </div>
      </Card>
      <Card testid="webhook-card" title="Webhook" desc="Incolla questi valori in Meta → WhatsApp → Configurazione → Webhook" icon={Webhook}
        right={data.webhook_verified_at && <span data-testid="webhook-verified-badge" className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#DCFCE7] text-[#15803D] flex items-center gap-1"><ShieldCheck size={12} />Verificato</span>}>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>URL di callback</Label>
            <div className="flex gap-2">
              <code data-testid="webhook-url-display" className="flex-1 font-mono text-sm bg-slate-900 text-[#25D366] rounded-md px-3 py-2.5 overflow-x-auto whitespace-nowrap">{webhookUrl}</code>
              <Button data-testid="webhook-url-copy-button" variant="outline" size="icon" className="h-10 w-10" onClick={() => copy(webhookUrl)}><Copy size={15} /></Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Token di verifica</Label>
            <div className="flex gap-2">
              <Input data-testid="webhook-verify-token-input" value={f.wa_verify_token || ""} onChange={(e) => setF({ ...f, wa_verify_token: e.target.value })} placeholder="Imposta un token segreto" className="font-mono text-sm" />
              <Button data-testid="webhook-generate-token-button" variant="outline" size="icon" className="h-10 w-10" onClick={genToken} title="Genera"><RefreshCw size={15} /></Button>
              <Button data-testid="webhook-copy-token-button" variant="outline" size="icon" className="h-10 w-10" onClick={() => copy(f.wa_verify_token || "")} title="Copia"><Copy size={15} /></Button>
            </div>
          </div>
          {!data.wa_app_secret_set && <p data-testid="webhook-signature-warning" className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">Imposta l'App Secret per verificare la firma (X-Hub-Signature-256) dei messaggi in arrivo in produzione.</p>}
          <p className="text-xs text-slate-500">Iscriviti al campo <code className="bg-slate-100 px-1 rounded">messages</code>. Ricordati di salvare dopo aver modificato il token.</p>
          <div className="flex justify-end"><Button data-testid="webhook-save-button" onClick={save} className="bg-[#0F172A] hover:bg-[#1E293B]"><Save size={15} className="mr-1.5" />Salva token</Button></div>
        </div>
      </Card>
      <Card testid="coexistence-card" title="Coexistence (app WhatsApp Business + CRM)" desc="Stesso numero sul cellulare e nel CRM, in contemporanea" icon={Smartphone}
        right={<PhoneStatusBadge />}>
        <div className="space-y-3 text-sm text-slate-600">
          <ConnectWhatsApp settings={data} />
          <PhoneReminder />
          <p>Il CRM è pronto per la Coexistence: i messaggi che scrivi dal cellulare compaiono qui come "Telefono (App Business)", e lo storico chat e i contatti vengono importati.</p>
          <ol className="list-decimal pl-5 space-y-1 text-xs">
            <li>App WhatsApp Business aggiornata (2.24.17+), numero attivo da almeno 7 giorni.</li>
            <li>Collega il numero tramite un partner Meta (es. 360dialog) o la tua app Meta con Embedded Signup "Collega app WhatsApp Business", senza ri-registrare il numero.</li>
            <li>In Meta → Webhook iscriviti ai campi: <code className="bg-slate-100 px-1 rounded">messages</code>, <code className="bg-slate-100 px-1 rounded">smb_message_echoes</code>, <code className="bg-slate-100 px-1 rounded">history</code>, <code className="bg-slate-100 px-1 rounded">smb_app_state_sync</code>.</li>
            <li>Avvia la sincronizzazione dello storico entro 24 ore dal collegamento.</li>
            <li>Apri l'app sul cellulare almeno una volta ogni 13 giorni.</li>
          </ol>
          <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">Limiti Meta: 20 messaggi/secondo, niente spunta blu OBA, i messaggi inviati da WhatsApp Desktop per Windows non vengono rispecchiati.</p>
          <CoexSync data={data} />
        </div>
      </Card>
      <MetaAppConfig />
    </div>
  );
};

const MetaAppConfig = () => {
  const origin = window.location.origin;
  const rows = [
    ["privacy", "URL privacy policy", `${origin}/privacy`],
    ["deletion", "URL eliminazione dati", `${origin}/eliminazione-dati`],
    ["domain", "Dominio app", window.location.hostname],
    ["category", "Categoria", "Business"],
  ];
  return (
    <Card testid="meta-app-config-card" title="Configurazione app Meta" desc="Da incollare in Meta for Developers → Impostazioni app → Di base" icon={FileCheck}>
      <div className="space-y-2">
        {rows.map(([id, label, value]) => (
          <div key={id} className="flex flex-wrap sm:flex-nowrap items-center gap-2">
            <span className="w-full sm:w-44 text-xs font-semibold text-slate-500">{label}</span>
            <code data-testid={`meta-config-${id}`} className="flex-1 min-w-0 font-mono text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 truncate">{value}</code>
            <Button data-testid={`meta-config-${id}-copy`} variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => copy(value)}><Copy size={13} /></Button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="w-full sm:w-44 text-xs font-semibold text-slate-500">Icona app 1024×1024</span>
          <a data-testid="meta-config-icon-download" href="/api/branding/icon/1024.png" download="icona-app-1024.png"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md bg-[#0F172A] text-white hover:bg-[#1E293B]"><Download size={13} />Scarica PNG</a>
          <span className="text-[11px] text-slate-400">Generata dal logo aziendale (o icona predefinita)</span>
        </div>
      </div>
    </Card>
  );
};
